import { getAssetIconBySymbol } from 'abis/set/asset-icons'
import { NetworkType, getSupportedNetworkTypeFromChainId } from 'abis/set/sets'
import { ZERO_EX_AFFILIATE_AVAX_ADDRESS } from 'app-constants'
import Big from 'big.js'
import type { WithInitialValue } from 'components/widgets/invest-component/interfaces/common'
import type { CurrencyMetadata, ProductMetadata } from 'indices-list'
import { type PrimitiveAtom, atom } from 'jotai'
import type { atomsWithQuery } from 'jotai-tanstack-query'
import type { AnatomyWithExtra } from 'services/query-v2/anatomy'
import { avalancheCurrencies, formatWeiToAmount } from 'utils'
import { type Address, zeroAddress } from 'viem'

export type PriceParams = {
  sellCurrency: CurrencyMetadata
  buyCurrency: CurrencyMetadata
  sellAmount: Big
  sellValue: Big
  enableSlippageProtection: boolean
  affiliateAddress: Address
  slippagePercentage: number
  chainId: NetworkType
  productAddress: Address
  userAddress: Address
}

export type OutputAmountResponse = {
  chainId: NetworkType
  currency: CurrencyMetadata
  buyAmount: Big
}

export type PriceStorage = {
  [chainId: number]: {
    [address: Address]: Big
  }
}

export type BuyAmountStorage = {
  [chainId: number]: {
    [address: Address]: Big
  }
}

type QueryAmountReturn = ReturnType<typeof atomsWithQuery<OutputAmountResponse>>

export type QueryAmountRequest = QueryAmountReturn['0']
export type QueryAmountDispatch = QueryAmountReturn['1']

export type OutputRequestAtoms = {
  params: PrimitiveAtom<PriceParams> & WithInitialValue<PriceParams>
  response: QueryAmountRequest
  dispatch: QueryAmountDispatch
  indexAnatomy: PrimitiveAtom<AnatomyWithExtra> & WithInitialValue<AnatomyWithExtra>
}

export type RawOutputAmountResponse = {
  buyAmount: string
  data?: Address
  to?: string
  gas?: string
}

export type RawQuotesResponse = {
  decodedUniqueId: string
  guaranteedPrice: string
  sellAmount: string
  transaction: {
    data: Address
    gas: string
    gasPrice: string
    to: string
    value: string
  }
} & RawOutputAmountResponse

export const defaultPriceCurrency: CurrencyMetadata = {
  address: zeroAddress,
  name: 'USD Coin',
  symbol: 'USDC',
  decimals: 6,
  icon: getAssetIconBySymbol('USDC'),
  defaultQuoteAmount: Big(0),
  chainId: NetworkType.Avalanche,
  type: 'token',
  coingeckoId: undefined,
  permit: undefined,
}

export const defaultPriceParams: PriceParams = {
  sellCurrency: avalancheCurrencies.USDC,
  buyCurrency: avalancheCurrencies.USDC,
  sellAmount: Big(0),
  sellValue: Big(0),
  enableSlippageProtection: true,
  affiliateAddress: ZERO_EX_AFFILIATE_AVAX_ADDRESS,
  slippagePercentage: 0.05, // TODO: take it from data-model after settings impl
  chainId: getSupportedNetworkTypeFromChainId(NetworkType.Avalanche),
  productAddress: avalancheCurrencies.CAI.address,
  userAddress: zeroAddress,
}

export const buyAmountsStorageAtom = atom<BuyAmountStorage>({})

export const amountSetterAtom = atom(null, (get, set, outputUpdate: OutputAmountResponse) => {
  const amounts = get(buyAmountsStorageAtom)
  set(buyAmountsStorageAtom, {
    ...amounts,
    [outputUpdate.chainId]: {
      ...amounts[outputUpdate.chainId],
      [outputUpdate.currency.address]: outputUpdate.buyAmount,
    },
  })
})

export const getInitialPriceParams = (
  params: ProductMetadata,
  inputCurrency: CurrencyMetadata,
): PriceParams => {
  return {
    ...defaultPriceParams,
    sellCurrency: inputCurrency,
    buyCurrency: params.productCurrency,
    sellAmount: Big(0),
    slippagePercentage: params.defaultSlippagePercentage,
    chainId: getSupportedNetworkTypeFromChainId(params.network),
    productAddress: params.address,
  }
}

export const parseOutput = (
  params: PriceParams,
  response: RawQuotesResponse,
): OutputAmountResponse => {
  return {
    chainId: params.chainId,
    currency: params.sellCurrency,
    buyAmount: formatWeiToAmount(response.buyAmount, params.buyCurrency.decimals),
  }
}
